import type { FC } from "react";

import Skeleton from "@/components/global/SkeletonComponent/client/SkeletonComponent";
import type SkeletonLengthInterface from "@/components/global/SkeletonComponent/interfaces/SkeletonLengthInterface";
import { cn } from "@/utils/common/styleHelper/utils";

interface SkeletonFlightPromotionCardsProps extends SkeletonLengthInterface {
  isAirline?: boolean;
  isShowTab?: boolean;
  classNames?: {
    base?: string;
    container?: string;
    image?: string;
    detail?: string;
    subBase?: string;
  };
  isSmallCard?: boolean;
}

const SkeletonFlightPromotionCardsComponent: FC<
  SkeletonFlightPromotionCardsProps
> = ({
  isAirline = true,
  length = 4,
  isShowTab = false,
  classNames = {
    base: "",
    container: "",
    image: "",
    detail: "",
    subBase: "",
  },
  isSmallCard = false,
}) => {
  const { base, container, image, detail, subBase } = classNames;
  return (
    <div
      className={cn(
        "flex w-full flex-col gap-4 overflow-auto no-scrollbar md:overflow-visible",
        base,
      )}
      id="forSkeletonSeoFlight"
    >
      <div className="flex w-full flex-col gap-4 md:gap-6">
        {isAirline ||
          (isShowTab && (
            <div className="flex h-[33px] w-full items-center md:h-[39px]">
              <Skeleton className="h-5 w-1/2 md:h-6 md:w-[30%]" />
            </div>
          ))}
        <div className={cn("flex size-full gap-4", subBase)}>
          {Array.from({ length }).map((_, index) => {
            return (
              <div
                key={index}
                className={cn(
                  "flex w-[314.77px] flex-none flex-col rounded-lg shadow-s md:w-72",
                  container,
                )}
              >
                {isAirline && (
                  <Skeleton
                    className={cn(
                      "h-[192px] w-full rounded-b-none rounded-t-lg md:h-[216px]",
                      image,
                    )}
                  />
                )}
                <div
                  className={cn(
                    "w-full rounded-b-lg bg-background-whiteBG p-4 md:pt-3",
                    isAirline ? "" : "rounded-t-lg shadow-s",
                    detail,
                  )}
                >
                  <div className="flex w-full flex-col gap-2">
                    <div
                      className={cn(
                        "flex w-full flex-col gap-1",
                        isAirline ? "" : "pb-2",
                      )}
                    >
                      <div className="flex h-6 w-full items-center gap-2">
                        <Skeleton className="size-6 flex-none rounded-full" />
                        <Skeleton className="h-3.5 w-full" />
                      </div>
                      <div className="flex w-full items-center justify-between">
                        <div className="flex size-full flex-col">
                          <div className="flex h-[33px] w-full items-center">
                            <Skeleton className="h-5 w-1/2" />
                          </div>
                          <div className="-mt-1 flex h-[26px] w-full items-center">
                            <Skeleton className="h-4 w-4/5" />
                          </div>
                        </div>
                        <Skeleton className="size-6 flex-none rounded-full" />
                        <div className="flex size-full flex-col">
                          <div className="flex h-[33px] w-full items-center justify-end">
                            <Skeleton className="h-5 w-1/2" />
                          </div>
                          <div className="-mt-1 flex h-[26px] w-full items-center justify-end">
                            <Skeleton className="h-4 w-4/5" />
                          </div>
                        </div>
                      </div>
                      <div className="flex h-[23px] w-full items-center">
                        <Skeleton className="h-3.5 w-full" />
                      </div>
                    </div>
                    {!isSmallCard && (
                      <div className="flex w-full items-end justify-between">
                        <div className="flex h-[23px] w-1/4 items-center">
                          <Skeleton className="h-3.5 w-full" />
                        </div>
                        <div className="flex w-1/2 items-end justify-end gap-2">
                          <div className="flex h-[26px] w-1/2 items-center">
                            <Skeleton className="h-4 w-full" />
                          </div>
                          <div className="flex w-full items-center justify-end gap-1">
                            <div
                              className={cn(
                                "flex h-[33px] w-full items-center",
                                isAirline ? "" : "h-[25px]",
                              )}
                            >
                              <Skeleton className="h-5 w-full" />
                            </div>
                            <div className="flex h-[25px] w-full items-center">
                              <Skeleton className="h-4 w-full" />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="hidden w-full items-center justify-end md:flex">
        <Skeleton className="mx-1 h-2 w-8 rounded-full" />
        {Array.from({ length: 4 }).map((__, idx) => (
          <Skeleton key={idx} className="mx-1 size-2 rounded-full last:mr-0" />
        ))}
      </div>
    </div>
  );
};

export default SkeletonFlightPromotionCardsComponent;
